<script setup lang="ts">
import BaseIcon from '@/components/ui/BaseIcon.vue';
import RoundedButton from '@/components/ui/RoundedButton.vue';

/** The departure and arrival of the journey, each opening the stop picker, with close and swap buttons. */
defineProps<{
  originLabel: string;
  /** Undefined until the passenger picks the arrival: the placeholder is shown instead. */
  destinationLabel?: string;
}>();

const emit = defineEmits<{ pickOrigin: []; pickDestination: []; swap: []; close: [] }>();
</script>

<template>
  <div class="itinerary-field">
    <div class="itinerary-field__card">
      <button class="itinerary-field__row" type="button" @click="emit('pickOrigin')">
        <BaseIcon name="square-stop" class="itinerary-field__icon" />
        <span class="itinerary-field__label">{{ originLabel }}</span>
        <BaseIcon name="chevron-right" class="itinerary-field__chevron" />
      </button>
      <button class="itinerary-field__row" type="button" @click="emit('pickDestination')">
        <BaseIcon name="square-stop" class="itinerary-field__icon" />
        <span
          class="itinerary-field__label"
          :class="{ 'itinerary-field__label--placeholder': !destinationLabel }"
        >
          {{ destinationLabel || $t('itinerary.chooseDestination') }}
        </span>
        <BaseIcon name="chevron-right" class="itinerary-field__chevron" />
      </button>
    </div>

    <div class="itinerary-field__actions">
      <RoundedButton
        :aria-label="$t('itinerary.close')"
        class="itinerary-field__action"
        icon="x"
        type="outline"
        @click="emit('close')"
      />
      <RoundedButton
        :aria-label="$t('itinerary.swap')"
        class="itinerary-field__action"
        icon="arrow-up-down"
        type="outline"
        @click="emit('swap')"
      />
    </div>
  </div>
</template>

<style lang="scss">
.itinerary-field {
  align-items: center;
  display: flex;
  gap: $spacing-2;

  &__card {
    background-color: $color-white;
    border: 2px solid $color-border-medium;
    border-radius: $radius-xl;
    box-shadow: 0 2px 24px rgba(0, 0, 0, 0.04);
    display: flex;
    flex: 1;
    flex-direction: column;
    min-width: 0;
    overflow: hidden;
    pointer-events: auto;
  }

  &__row {
    align-items: center;
    background-color: $color-white;
    border: none;
    color: $color-neutral-highest;
    cursor: pointer;
    display: flex;
    gap: $spacing-2;
    height: 56px;
    padding: $spacing-2 $spacing-2 $spacing-2 $spacing-3;
    text-align: left;
    width: 100%;

    & + & {
      border-top: 1px solid $color-border-medium;
    }

    &:active {
      background-color: $color-neutral-lower;
    }
  }

  &__icon {
    flex-shrink: 0;
    font-size: 20px;
  }

  &__label {
    flex: 1;
    font: $label-medium;
    letter-spacing: $label-medium-spacing;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &--placeholder {
      color: $color-neutral-higher;
    }
  }

  &__chevron {
    color: $color-neutral-higher;
    flex-shrink: 0;
    font-size: 24px;
  }

  &__actions {
    align-self: stretch;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    pointer-events: auto;
  }

  &__action .rounded-button__icon {
    font-size: 16px;
  }
}
</style>
